/**
 * Une ligne de résultat de la palette Ctrl+K.
 *
 * Ligne 56px (utilisable au doigt sur tablette en cave, pas que clavier). Rend
 * une OPTION de listbox : la sélection est VIRTUELLE (le focus reste dans le
 * champ, cf pattern `aria-activedescendant`), donc l'option ne prend jamais le
 * focus clavier — elle réagit au survol souris et au clic.
 */

import { RECHERCHE_TYPE_LABELS, type RechercheResultat } from "@champ-erp/contracts";
import { Badge } from "@champ-erp/ui";

import { RECHERCHE_TYPE_ICONS } from "./rechercheTypeMeta.js";

/** ID DOM stable d'une option, pour `aria-activedescendant` + `scrollIntoView`. */
export function optionId(index: number): string {
  return `command-palette-option-${index}`;
}

export interface ResultatRechercheProps {
  resultat: RechercheResultat;
  /** Index dans la liste APLATIE (tous groupes confondus). */
  index: number;
  /** True si c'est la ligne active (surlignée + pointée par aria-activedescendant). */
  actif: boolean;
  /**
   * Affiche la pastille de type. Utile dans « Récents » (types mélangés) ; inutile
   * dans les résultats de recherche où l'en-tête de groupe porte déjà le type.
   */
  afficherType: boolean;
  onSelect: () => void;
  onSurvol: () => void;
}

export function ResultatRecherche({
  resultat,
  index,
  actif,
  afficherType,
  onSelect,
  onSurvol,
}: ResultatRechercheProps) {
  const Icone = RECHERCHE_TYPE_ICONS[resultat.type];
  return (
    <div
      id={optionId(index)}
      role="option"
      aria-selected={actif}
      // Survol = pré-sélection (comportement attendu d'une palette de commande).
      // React ignore un setState de même valeur → pas de re-render en rafale.
      onMouseMove={onSurvol}
      onClick={onSelect}
      className={[
        "flex min-h-[56px] cursor-pointer items-center gap-3 px-4",
        actif ? "bg-champagne-100" : "bg-transparent",
      ].join(" ")}
    >
      <span
        className={[
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
          actif ? "bg-white text-champagne-700" : "bg-champagne-50 text-champagne-600",
        ].join(" ")}
      >
        <Icone size={18} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-aubergine-900">
          {resultat.libelle}
        </span>
        {resultat.sous_libelle && (
          <span className="block truncate text-[12.5px] text-champagne-700">
            {resultat.sous_libelle}
          </span>
        )}
      </span>
      {afficherType && (
        <Badge variant="neutral" size="sm">
          {RECHERCHE_TYPE_LABELS[resultat.type]}
        </Badge>
      )}
    </div>
  );
}
